import axios from '../services/api.service';

export interface Company {
    id: string;
    name: string;
    description?: string;
    website?: string;
    phone?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
    tax_id?: string;
    stripe_customer_id?: string;
    stripe_subscription_id?: string;
    stripe_subscription_item_id?: string;
    stripe_subscription_quantity?: number;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
    // Bring-your-own LLM key. The server never returns a stored key, encrypted or
    // otherwise — only whether one is present, plus which provider is active.
    anthropic_api_key_configured?: boolean;
    openai_api_key_configured?: boolean;
    use_anthropic_api_key?: boolean;
    use_openai_api_key?: boolean;
}

export interface CreateCompanyRequest {
    name: string;
    description?: string;
    website?: string;
    phone?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
    tax_id?: string;
}

export interface UpdateCompanyRequest extends CreateCompanyRequest {
    // Bring-your-own LLM key, write side. Omit a field to leave it as stored —
    // the server treats an absent field as "no change" and an empty string as
    // "clear the stored key", so never send '' for a key you meant to keep.
    // Changing any of these four requires a company admin.
    anthropic_api_key?: string;
    openai_api_key?: string;
    use_anthropic_api_key?: boolean;
    use_openai_api_key?: boolean;
}

export interface CompanySetupResponse {
    has_company: boolean;
    company?: Company;
}

export interface CompanySubscriptionStatus {
    subscription_status: 'premium' | 'free';
    company?: Company;
    message: string;
}

export const companyGetInfo = async (): Promise<Company> => {
    const response = await axios.get('/api/company');
    return response.data.company;
};

export const companyCreate = async (data: CreateCompanyRequest): Promise<{ company_id: string }> => {
    const response = await axios.post('/api/company', data);
    return response.data;
};

export const companyUpdate = async (data: UpdateCompanyRequest): Promise<{ company: Company }> => {
    const response = await axios.put('/api/company', data);
    return response.data;
};

export const companyCheckSetup = async (): Promise<CompanySetupResponse> => {
    const response = await axios.get('/api/company/setup');
    return response.data;
};

export const companyPostCheckout = async (data: object): Promise<any> => {
    try {
        const response = await axios.post('/api/company/checkout', data);
        return response.data;
    } catch (error) {
        console.error('Error in companyPostCheckout:', error);
        throw error; // Re-throw the error instead of returning 'error'
    }
};

export const companyPostPortal = async (data: object): Promise<any> => {
    try {
        const response = await axios.post('/api/company/portal', data);
        return response.data;
    } catch (error) {
        console.error('Error in companyPostPortal:', error);
        throw error; // Re-throw the error instead of returning 'error'
    }
};

export const companyGetSubscriptionStatus = async (): Promise<{ success: boolean; data: CompanySubscriptionStatus } | { success: false; error: any }> => {
    try {
        console.log('=== COMPANY SUBSCRIPTION API DEBUG START ===');
        console.log('API: Making GET request to /api/company/subscription-status');

        // Use the new endpoint that checks real-time Stripe status
        const { data } = await axios.get('/api/company/subscription-status');

        console.log('API: Company subscription status response:', data);
        console.log('API: Subscription status:', data.subscription_status);
        console.log('API: Company data:', data.company);
        console.log('API: Message:', data.message);

        const result = {
            success: true,
            data: {
                subscription_status: data.subscription_status,
                company: data.company,
                message: data.message
            }
        };

        console.log('API: Returning result:', result);
        console.log('=== COMPANY SUBSCRIPTION API DEBUG END ===');

        return result;
    } catch (error: any) {
        console.log('=== COMPANY SUBSCRIPTION API ERROR START ===');
        console.error('API: Error fetching company subscription status:', error);
        console.error('API: Error response:', error.response);
        console.error('API: Error status:', error.response?.status);
        console.error('API: Error data:', error.response?.data);

        // Si aucune company n'existe (404), on retourne "Free" par défaut
        if (error.response?.status === 404) {
            console.log('API: No company found (404), returning free tier');
            const result = {
                success: true,
                data: {
                    subscription_status: 'free' as const,
                    message: 'No company found - defaulting to free tier'
                }
            };
            console.log('API: Returning free tier result:', result);
            console.log('=== COMPANY SUBSCRIPTION API ERROR END ===');
            return result;
        }

        console.log('API: Returning error result');
        console.log('=== COMPANY SUBSCRIPTION API ERROR END ===');
        return { success: false, error };
    }
}; 